import { cloneDeep } from 'lodash';
import { buffers, eventChannel, EventChannel } from 'redux-saga';
import {
  actionChannel,
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  SagaGenerator,
} from 'typed-redux-saga';
import { v4 } from 'uuid';

import { getResourceManager } from '../../resources';
import { iDBActService } from '../../resources/iDBAct';
import { IAction } from '../../types/redux';
import {
  countDeltaToApply,
  prepareDeltaEventsBeforeCount,
  transformConflictsToApply,
} from '../../utils/EventTrain/countDeltaToApply';
import { beginSession, createSyncState, peekActiveSession } from '../../utils/SyncGroup/SyncGroup';
import { SyncSession } from '../../utils/SyncGroup/SyncSession';
import { sameToken, TSyncSessionToken, TSyncStateInfo } from '../../utils/SyncGroup/session.types';
import { classifySyncFailure, isRetryableFailure } from '../../utils/sync/classifySyncFailure';
import { innerApplyRemote } from '../editor';
import { TAppState } from '../types';
import { getUserInfo } from '../user/user.actions';

import { isLogoutIntentActive, setLogoutIntent } from './logoutIntentState';
import { commitSyncState, resetSyncState, syncReconcile } from './sync.actions';
import {
  IOnDeltaReceivedAction,
  ISyncHealthAction,
  ISyncReconcileAction,
  LOGOUT_INTENT,
  ON_DELTA_RECEIVED,
  SYNC_CMD_DISABLE,
  SYNC_CMD_ENABLE,
  SYNC_HEALTH,
  SYNC_RECONCILE,
  SYNC_STATE_ASK_DELTA,
  SyncStatus,
  TSyncStatusReason,
} from './sync.types';

const PROBE_BACKOFF_MIN = 5000;
const PROBE_BACKOFF_MAX = 60000;

// Дождаться гидратации сессии внутри саги.
const waitReady = (session: SyncSession) => session.ready;

// --- delta / ask-delta (session-fenced, из MR-1; без изменений) ---

export function* syncStateAskDelta(action: IAction<{ session?: TSyncSessionToken }>) {
  // Изоляция (MR-3 review C5): takeEvery-worker не должен уронить root all().
  // train.checkDelta() может реджектнуться (сеть/парс) — глушим здесь.
  try {
    const session = peekActiveSession();
    if (!session) {
      return;
    }
    if (action?.payload?.session && !sameToken(action.payload.session, session.token)) {
      return;
    }
    yield* call(waitReady, session);
    if (!session.isCurrent()) {
      return;
    }
    const { train } = session;
    if (!train) {
      return;
    }
    yield* call(train.checkDelta.bind(train));
  } catch (error) {
    console.error('Error during syncStateAskDelta', error);
  }
}

export function* deltaWorker(action: IAction<IOnDeltaReceivedAction>) {
  try {
    const { payload } = action;
    if (!payload || !payload?.remote || !payload?.local) {
      return;
    }

    const session = peekActiveSession();
    if (!session) {
      return;
    }
    if (payload.session && !sameToken(payload.session, session.token)) {
      return;
    }

    const { conflictState } = yield* select((state: TAppState) => state.sync);
    const { local } = payload;
    const remote = prepareDeltaEventsBeforeCount({ events: payload.remote, notes: payload.notes });

    const registry = cloneDeep(conflictState.registry);
    const conflicts = cloneDeep(conflictState.conflicts);
    const dispenser = cloneDeep(conflictState.dispenser);

    const results = countDeltaToApply({
      dispenser,
      registry,
      conflicts,
      remoteEvents: remote,
      localEvents: local,
    });

    const transformed = transformConflictsToApply(results.conflicts);
    const applyToClient = [...results.applyOnClient, ...transformed.resolvedForClient];

    if (session.isCurrent() && applyToClient.length > 0) {
      yield* put(innerApplyRemote({ session: session.token, events: applyToClient }));
    }

    const unResolvedConflictsCount = Object.values(results.conflicts).filter(
      (c) => !c.resolvedTo,
    ).length;
    if (unResolvedConflictsCount > 0) {
      console.log('Unresolved conflicts:', unResolvedConflictsCount);
    }

    if (session.isCurrent()) {
      const { train } = session;
      if (train) {
        train.setConflictLastIdToLastUpdateId();
        train.run();
      }
    }
  } catch (error) {
    console.log('Error during deltaWorker', error);
  }
}

// --- supervisor: reconcile (control-plane, §2.7 / decision-table §3) ---

// Хелпер коммита статуса с токеном текущей сессии.
function* commit(
  token: TSyncSessionToken,
  status: SyncStatus,
  opts?: { isEnabled?: boolean; reason?: TSyncStatusReason },
) {
  yield* put(
    commitSyncState({
      session: token,
      status,
      isEnabled: opts?.isEnabled,
      reason: opts?.reason ?? null,
    }),
  );
}

// Тихая проба с backoff-циклом: пока исход транзиентный (сеть/5xx/408/429) — держим
// PAUSE(transport) и повторяем. Отмена цикла: (а) takeLatest отменяет reconcile при новом
// SYNC_RECONCILE (напр. environment-транзиция) — backoff сбрасывается сменой окружения;
// (б) явная проверка session.isCurrent() ПОСЛЕ delay — чтобы PROJECT_CLEAN/смена проекта
// не оставили вечный опрос (MR-3 review W3). Возвращает первый НЕ-транзиентный результат
// либо null, если сессия перестала быть текущей.
function* probeWithBackoff<T>(
  probe: () => Promise<T & { kind: string; status?: number }>,
  session: SyncSession,
  token: TSyncSessionToken,
  isEnabled: boolean | undefined,
) {
  let attempt = 0;
  while (true) {
    const result = yield* call(probe);
    const cls = classifySyncFailure(result as { kind?: any; status?: number });
    if (!isRetryableFailure(cls)) {
      return result;
    }

    yield* commit(token, SyncStatus.PAUSE, { isEnabled, reason: 'transport' });

    attempt += 1;
    const base = Math.min(PROBE_BACKOFF_MIN * 2 ** (attempt - 1), PROBE_BACKOFF_MAX);
    const jitter = base * (0.8 + Math.random() * 0.4);
    yield* delay(jitter);

    if (!session.isCurrent()) {
      return null;
    }
  }
}

// Блокировать train активной сессии (safety) — только ужесточение, без unblock.
function blockTrain(session: SyncSession, flags: { isOffline?: boolean; isNotVisible?: boolean }) {
  session.train?.setIsBlocked({ isBlocked: true, ...flags });
}

export function* reconcileWorker(_action?: IAction<ISyncReconcileAction>) {
  const session = peekActiveSession();
  if (!session) {
    return;
  }

  yield* call(waitReady, session);
  if (!session.isCurrent()) {
    return;
  }

  const { token } = session;

  // Известное локальное isEnabled, доступное общему catch (MR-3 review W2): чтобы
  // аварийный PAUSE не терял состояние тумблера и UI не откатывался в INACTIVE.
  let knownEnabled: boolean | undefined;

  try {
    const snapshot: TSyncStateInfo = session.syncStateSnapshot();

    // 1. Снапшот записи (не-enabled ветки — конец по таблице).
    if (snapshot.kind === 'storage-error') {
      blockTrain(session, {});
      yield* commit(token, SyncStatus.PAUSE, { reason: 'local-storage-error' });
      return;
    }
    if (snapshot.kind === 'missing') {
      yield* commit(token, SyncStatus.INACTIVE, { isEnabled: false });
      return;
    }
    if (snapshot.kind === 'invalid') {
      console.warn('reconcile: invalid syncState record (recovery is I6/F11)');
      yield* commit(token, SyncStatus.INACTIVE, { isEnabled: false });
      return;
    }

    const syncState = snapshot.state; // enabled | disabled
    const localEnabled = syncState.isSyncEnabled;
    knownEnabled = localEnabled;

    // Блокируем train на всё время проверок (safety, MR-3 review W4): пока не подтверждены
    // auth+eligibility, train не должен слать apply/delta. Разблокирует только resume() на
    // ACTIVE-пути; все PAUSE-ветки оставляют его заблокированным.
    blockTrain(session, {});

    // Намерение выйти: держим PAUSE(auth-required), не пробим/не резюмим.
    if (isLogoutIntentActive()) {
      yield* commit(token, SyncStatus.PAUSE, { isEnabled: localEnabled, reason: 'auth-required' });
      return;
    }

    // 2. Environment (читаем напрямую — начальное состояние, не транзиция).
    if (!navigator.onLine) {
      blockTrain(session, { isOffline: true });
      yield* commit(token, SyncStatus.PAUSE, { isEnabled: localEnabled, reason: 'offline' });
      return;
    }
    if (document.hidden) {
      blockTrain(session, { isNotVisible: true });
      yield* commit(token, SyncStatus.PAUSE, { isEnabled: localEnabled, reason: 'hidden' });
      return;
    }

    const manager = getResourceManager();

    // 3. Auth: если не авторизованы — тихая проба.
    const user = yield* select((state: TAppState) => state.user);
    if (!user.isAuth) {
      const info = yield* probeWithBackoff(
        manager.UserService.probeInfo.bind(manager.UserService),
        session,
        token,
        localEnabled,
      );
      if (!info || !session.isCurrent()) {
        return;
      }
      // ok, но без customer — сервер не подтвердил личность → трактуем как auth-required.
      if (info.kind === 'ok' && info.data?.customer) {
        yield* put(getUserInfo.success({ customer: info.data.customer }));
      } else if (info.kind === 'http' && info.status === 401) {
        yield* commit(token, SyncStatus.PAUSE, {
          isEnabled: localEnabled,
          reason: 'auth-required',
        });
        return;
      } else if (info.kind === 'ok') {
        yield* commit(token, SyncStatus.PAUSE, {
          isEnabled: localEnabled,
          reason: 'auth-required',
        });
        return;
      } else {
        console.error('reconcile: auth probe returned an unexpected result', info);
        yield* commit(token, SyncStatus.PAUSE, {
          isEnabled: localEnabled,
          reason: 'eligibility-error',
        });
        return;
      }
    }

    // 4. Eligibility: существует ли проект на сервере.
    const { project } = yield* select((state: TAppState) => state.editor);
    if (!project || project.id !== token.projectId) {
      return;
    }
    const exists = yield* probeWithBackoff(
      () => manager.ProjectService.probeExists({ projectId: token.projectId }),
      session,
      token,
      localEnabled,
    );
    if (!exists || !session.isCurrent()) {
      return;
    }
    if (exists.kind !== 'ok') {
      // 401 на eligibility — это потеря auth, а НЕ ошибка eligibility (MR-3 review W1).
      const reason =
        exists.kind === 'http' && exists.status === 401 ? 'auth-required' : 'eligibility-error';
      if (reason === 'eligibility-error') {
        console.error('reconcile: eligibility probe failed', exists);
      }
      yield* commit(token, SyncStatus.PAUSE, { isEnabled: localEnabled, reason });
      return;
    }

    // Обязательное boolean-поле: если сервер вернул ok без него — это невалидный
    // ответ, а НЕ "проекта нет" (MR-3 review W1). Иначе disabled-проект ушёл бы в
    // INACTIVE, а enabled — предложил бы повторный init существующего remote.
    if (typeof exists.data?.doesProjectExist !== 'boolean') {
      console.error('reconcile: eligibility ok without boolean doesProjectExist', exists);
      yield* commit(token, SyncStatus.PAUSE, {
        isEnabled: localEnabled,
        reason: 'eligibility-error',
      });
      return;
    }
    const { doesProjectExist } = exists.data;

    // Decision table §3.
    if (localEnabled && !doesProjectExist) {
      // enabled локально, но на сервере нет — PAUSE(eligibility-error), isEnabled НЕ трогаем.
      yield* commit(token, SyncStatus.PAUSE, { isEnabled: true, reason: 'eligibility-error' });
      return;
    }

    if (!localEnabled) {
      if (doesProjectExist && syncState.lastUpdateId) {
        // re-enable: disabled локально + exists + есть lastUpdateId → включаем (durable).
        syncState.isSyncEnabled = true;
        try {
          yield* call(iDBActService.saveOne, syncState.toStorage());
        } catch (error) {
          // Провал персиста re-enable → откат in-memory + PAUSE(local-storage-error).
          syncState.isSyncEnabled = false;
          console.error('reconcile: re-enable persist failed', error);
          yield* commit(token, SyncStatus.PAUSE, {
            isEnabled: false,
            reason: 'local-storage-error',
          });
          return;
        }
      } else {
        // disabled + (нет проекта ИЛИ нет lastUpdateId) → INACTIVE.
        yield* commit(token, SyncStatus.INACTIVE, { isEnabled: false });
        return;
      }
    }

    // 5. Запуск: resume() — атомарный старт одного цикла (снимает блок).
    if (!session.isCurrent()) {
      return;
    }
    const outcome = session.train?.resume() ?? 'disposed';
    if (outcome === 'started' || outcome === 'empty-idle') {
      yield* commit(token, SyncStatus.ACTIVE, { isEnabled: true });
    } else if (outcome === 'blocked-hidden') {
      yield* commit(token, SyncStatus.PAUSE, { isEnabled: true, reason: 'hidden' });
    } else {
      yield* commit(token, SyncStatus.INACTIVE, { isEnabled: false });
    }
  } catch (error) {
    // Изолируем любые исключения reconcile: takeLatest-worker НЕ должен уронить
    // syncSagaWatch → root all() → остальные саги (MR-3 review C5). Красим
    // PAUSE(local-storage-error) в текущей сессии.
    console.error('Error during reconcile', error);
    if (session.isCurrent()) {
      yield* commit(token, SyncStatus.PAUSE, {
        isEnabled: knownEnabled,
        reason: 'local-storage-error',
      });
    }
  }
}

// --- serialized enable/disable commands (замена INIT_SYNC/DESTROY_SYNC) ---

// Контракт с commandActor (MR-3 review r2): хендлер возвращает shouldReconcile.
// false ⇒ хендлер УЖЕ закоммитил терминальный/sticky-статус (напр. persist-fail
// PAUSE(local-storage-error)), и actor НЕ должен запускать reconcile, иначе тот прочитает
// in-memory state и затрёт честный статус. true ⇒ обычный путь, actor доводит через reconcile.
export function* handleEnable(): SagaGenerator<boolean> {
  try {
    const { project } = yield* select((state: TAppState) => state.editor);
    if (!project) {
      return true;
    }
    const projectId = project.id;
    const user = yield* select((state: TAppState) => state.user);

    const session = peekActiveSession();
    if (!session || session.token.projectId !== projectId) {
      return true;
    }
    yield* call(waitReady, session);
    if (!user.isAuth) {
      return true;
    }

    const manager = getResourceManager();
    const response = yield* call(manager.SyncService.initSync.bind(manager.SyncService), {
      eventId: v4(),
      project,
    });
    if (!response.isOk) {
      console.error('Cannot enable sync, server response not ok');
      return true;
    }

    // Fence перед локальной записью (NEW-C1).
    if (!session.isCurrent()) {
      return false;
    }
    const snapshot = session.syncStateSnapshot();
    let state = snapshot.kind === 'enabled' || snapshot.kind === 'disabled' ? snapshot.state : null;
    const prevEnabled = state ? state.isSyncEnabled : false;
    const prevLastUpdateId = state ? state.lastUpdateId : null;
    // Весь post-server persistence path (включая createSyncState) под одним try: любой сбой
    // локального хранилища после успешного initSync — единый sticky-PAUSE путь (MR-3 review r2).
    try {
      if (!state) {
        state = yield* call(createSyncState, projectId, {
          isSyncEnabled: true,
          lastUpdateId: response.lastUpdateId,
        });
      }
      // Очищаем очередь: initSync отправил ПОЛНЫЙ снапшот проекта (со всеми правками), поэтому
      // pending-события уже в нём — иначе новый train применит их повторно (MR-3 review C4,
      // дубликат/PK-conflict/500 на сервере без дедупликации).
      // ЗАЗОР (F36, deferral): правка, сделанная в окно round-trip initSync, персистится в
      // проект, но не заряжается (состояние ещё disabled) и не входит в snapshot@T0 → на сервер
      // не попадёт. Фикс требует состояния «enabling»/идемпотентного init (F34/I6). См. AUDIT.md.
      state.collectChanges({ isSyncEnabled: true, lastUpdateId: response.lastUpdateId, list: [] });
      state.commitChanges();
      yield* call(iDBActService.saveOne, state.toStorage());
    } catch (error) {
      // Сервер уже включён (initSync OK), но локальная запись не сохранилась → durable
      // рассинхрон, на сервере остался orphan remote. Откатываем in-memory к прежнему значению,
      // чтобы ПОСЛЕДУЮЩИЙ reconcile (visibility/health) не увидел enabled и не показал ACTIVE,
      // скрыв сбой; коммитим sticky PAUSE(local-storage-error) и возвращаем false (без reconcile).
      // Recovery — I6/F11.
      console.error('handleEnable: local persist failed after server init', error);
      if (state) {
        state.collectChanges({
          isSyncEnabled: prevEnabled,
          lastUpdateId: prevLastUpdateId,
          list: [],
        });
        state.commitChanges();
      }
      if (session.isCurrent()) {
        yield* commit(session.token, SyncStatus.PAUSE, {
          isEnabled: true,
          reason: 'local-storage-error',
        });
      }
      return false;
    }

    if (!session.isCurrent()) {
      return false;
    }
    // Пересоздаём сессию (гидратация из enabled-записи → train); reset слайса на новый токен.
    // reconcile доведёт до ACTIVE — его запускает commandActor по возврату true.
    const next = beginSession(projectId);
    yield* put(resetSyncState({ session: next.token }));
    return true;
  } catch (error) {
    console.log('Error during handleEnable', error);
    return false;
  }
}

export function* handleDisable(): SagaGenerator<boolean> {
  try {
    const { project } = yield* select((state: TAppState) => state.editor);
    if (!project) {
      return true;
    }
    const projectId = project.id;
    const user = yield* select((state: TAppState) => state.user);

    const session = peekActiveSession();
    if (!session || session.token.projectId !== projectId) {
      return true;
    }
    yield* call(waitReady, session);
    if (!user.isAuth) {
      return true;
    }

    const manager = getResourceManager();
    const response = yield* call(
      manager.ProjectService.deleteOneById.bind(manager.ProjectService),
      projectId,
    );
    if (!response.isOk) {
      console.error('Cannot disable sync, server response not ok');
      return true;
    }

    // finalize (не totalStop): проект удалён на сервере — поздние завершения in-flight
    // apply НЕ должны ретраить/эмитить health/рестартовать watch против удалённого проекта
    // (MR-3 review C2). finalize глушит charge/run/checkDelta/emit/health терминально;
    // уже летящий apply завершается, но его late side-effects подавлены.
    const { train } = session;
    if (train) {
      train.finalize();
    }

    if (!session.isCurrent()) {
      return false;
    }
    const snapshot = session.syncStateSnapshot();
    let state = snapshot.kind === 'enabled' || snapshot.kind === 'disabled' ? snapshot.state : null;
    const prevEnabled = state ? state.isSyncEnabled : true;
    const prevLastUpdateId = state ? state.lastUpdateId : null;
    // Весь post-server persistence path (включая createSyncState) под одним try (MR-3 review r2).
    try {
      if (!state) {
        state = yield* call(createSyncState, projectId, {
          isSyncEnabled: false,
          lastUpdateId: null,
        });
      }
      // Очередь тоже чистим: проекта на сервере больше нет, pending-события отправлять некуда.
      state.collectChanges({ isSyncEnabled: false, lastUpdateId: null, list: [] });
      state.commitChanges();
      yield* call(iDBActService.saveOne, state.toStorage());
    } catch (error) {
      // Проект удалён на сервере, но локальная запись persist не прошла → durable рассинхрон.
      // Откатываем in-memory к прежнему (enabled), чтобы последующий reconcile не показал
      // INACTIVE, скрыв рассинхрон; коммитим sticky PAUSE(local-storage-error), без reconcile.
      // Recovery — I6/F11.
      console.error('handleDisable: local persist failed after server delete', error);
      if (state) {
        state.collectChanges({
          isSyncEnabled: prevEnabled,
          lastUpdateId: prevLastUpdateId,
          list: [],
        });
        state.commitChanges();
      }
      if (session.isCurrent()) {
        yield* commit(session.token, SyncStatus.PAUSE, {
          isEnabled: false,
          reason: 'local-storage-error',
        });
      }
      return false;
    }

    if (!session.isCurrent()) {
      return false;
    }
    yield* commit(session.token, SyncStatus.INACTIVE, { isEnabled: false });
    return true;
  } catch (error) {
    console.log('Error during handleDisable', error);
    return false;
  }
}

function* commandActor(): SagaGenerator<void> {
  // expanding-буфер: команды enable/disable НЕ теряем (в отличие от reconcile, который
  // коалесцируется takeLatest'ом).
  const channel = yield* actionChannel(
    [SYNC_CMD_ENABLE, SYNC_CMD_DISABLE],
    buffers.expanding<IAction<unknown>>(),
  );
  while (true) {
    const action = yield* take(channel);
    // Изоляция тела цикла (MR-3 review C5): исключение из команды НЕ должно завершить
    // fork → уронить root all(). handleEnable/handleDisable ловят внутри, это второй рубеж.
    try {
      const shouldReconcile =
        action.type === SYNC_CMD_ENABLE ? yield* handleEnable() : yield* handleDisable();
      // reconcile доводит статус/старт — НО только если хендлер не закоммитил sticky-статус
      // сам (persist-fail): иначе reconcile прочитает in-memory и затрёт честный PAUSE
      // (MR-3 review r2).
      if (shouldReconcile) {
        yield* put(syncReconcile({ reason: 'command' }));
      }
    } catch (error) {
      console.error('Error during commandActor iteration', error);
    }
  }
}

// --- health-мост (§2.5) ---

export function* healthWorker(action: IAction<ISyncHealthAction>) {
  try {
    const session = peekActiveSession();
    if (!session || !session.isCurrent()) {
      return;
    }
    if (action.payload?.session && !sameToken(action.payload.session, session.token)) {
      return;
    }

    if (action.payload?.kind === 'recoverable-failure') {
      // Транспорт нездоров — PAUSE(transport). Backoff-ретрай остаётся у train (§2.5).
      yield* commit(session.token, SyncStatus.PAUSE, { isEnabled: true, reason: 'transport' });
    } else if (action.payload?.kind === 'terminal-failure') {
      // Терминальный (не-ретраемый) сбой apply — train встал, сам не восстановится.
      // PAUSE(sync-error), чтобы UI не остался ACTIVE при сломанном синке (см. F29/F37).
      yield* commit(session.token, SyncStatus.PAUSE, { isEnabled: true, reason: 'sync-error' });
    } else {
      // recovered (агрегатно: оба источника здоровы) → переоценить (resume → ACTIVE).
      yield* put(syncReconcile({ reason: 'health' }));
    }
  } catch (error) {
    console.log('Error during healthWorker', error);
  }
}

// --- logout intent (§2.7) ---

function* logoutIntentWorker() {
  // Изоляция (MR-3 review C5): takeEvery-worker не должен уронить root all().
  try {
    setLogoutIntent();
    const session = peekActiveSession();
    if (session?.isCurrent()) {
      blockTrain(session, {});
    }
    yield* put(syncReconcile({ reason: 'logout' }));
  } catch (error) {
    console.error('Error during logoutIntentWorker', error);
  }
}

// --- environment-watcher (единственный владелец каналов, §2.7) ---

function createEventChannel<T extends NonNullable<unknown>>(
  subscribe: (emit: (value: T) => void) => () => void,
  initialValue: T,
): EventChannel<T> {
  return eventChannel<T>((emit) => {
    const unsubscribe = subscribe(emit);
    emit(initialValue);
    return unsubscribe;
  });
}

function createVisibilityChannel(): EventChannel<boolean> {
  return createEventChannel<boolean>((emit) => {
    const handler = () => emit(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, !document.hidden);
}

function createNetworkStatusChannel(): EventChannel<boolean> {
  return createEventChannel<boolean>((emit) => {
    const onlineHandler = () => emit(true);
    const offlineHandler = () => emit(false);
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, navigator.onLine);
}

function* environmentWatcher(): SagaGenerator<void> {
  // Начальное состояние отражено в конструкторе train'а (buildTrain читает
  // navigator.onLine/document.hidden) и в reconcile — канал обрабатывает ТОЛЬКО
  // транзиции. Immediate-emit каналов дропается (буфера нет) — не транзиция.
  const network = createNetworkStatusChannel();
  const visibility = createVisibilityChannel();

  try {
    while (true) {
      const { online, visible } = yield* race({
        online: take(network),
        visible: take(visibility),
      });

      // Изоляция тела цикла (MR-3 review C5): исключение НЕ должно дойти до finally
      // (закрытие каналов) и завершить fork → уронить root all(). Ловим и продолжаем.
      try {
        const session = peekActiveSession();

        // На offline/hidden немедленно блокируем активный train (safety), затем reconcile.
        if (online === false && session?.isCurrent()) {
          blockTrain(session, { isOffline: true });
        }
        if (visible === false && session?.isCurrent()) {
          blockTrain(session, { isNotVisible: true });
        }
        // На visible снимаем блок видимости (environment владеет visibility; resume() его НЕ
        // трогает — иначе train навсегда остался бы blocked-hidden, MR-3 review C1). Старт —
        // через reconcile→resume ниже. isOffline при online снимет сам resume().
        if (visible === true && session?.isCurrent()) {
          session.train?.markVisible();
        }

        yield* put(
          syncReconcile({ reason: typeof online !== 'undefined' ? 'network' : 'visibility' }),
        );
      } catch (error) {
        console.error('Error during environmentWatcher iteration', error);
      }
    }
  } finally {
    network.close();
    visibility.close();
  }
}

export function* syncSagaWatch() {
  yield* takeEvery(SYNC_STATE_ASK_DELTA, syncStateAskDelta);
  yield* takeEvery(ON_DELTA_RECEIVED, deltaWorker);

  yield* takeLatest(SYNC_RECONCILE, reconcileWorker);
  yield* takeEvery(SYNC_HEALTH, healthWorker);
  yield* takeEvery(LOGOUT_INTENT, logoutIntentWorker);

  yield* fork(commandActor);
  yield* fork(environmentWatcher);
}
